import React from 'react';
import Link from 'next/link';
import { Select } from 'antd';
import { useStateContext } from '@/context/StateContext';
import Image from 'next/image';
import { dmarcProducts } from '@/data/dmarc-products';
import { motion } from 'framer-motion';

const { Option } = Select;

const DMARCPricing = ({ selectedOption, onOptionChange, getSelectedProductOption }) => {
  const { onAddDmarc, setShowCart } = useStateContext();

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  const handleBuyNow = (product) => {
    const selectedProductOption = getSelectedProductOption(product.id);
    const formattedProduct = {
      ...product,
      name: `DMARC ${product.name}`,
      type: 'dmarc',
      features: [
        {
          name: 'Emails',
          type: 'select',
          options: product.options.map((opt) => ({
            value: opt.emails,
            price: opt.price,
            msrp: opt.msrp,
            annualPrice: opt.annualPrice,
          })),
        },
        {
          name: 'Domains',
          value: product.id === 'dmarc-plus' ? '2' : '4',
          type: 'static',
        },
      ],
    };

    onAddDmarc(formattedProduct, 1, selectedOption[product.id]);
    setShowCart(true);
  };

  // Handle synchronized dropdown changes
  const handleOptionChange = (value, productId) => {
    // Create new options object with the current state
    const newOptions = { ...selectedOption };

    // Update the changed product's value
    newOptions[productId] = value;

    // Get the selected value as a number for comparison
    const selectedValue = parseInt(value.replace(/,/g, ''));

    // Find the other product and update its value
    const otherProduct = dmarcProducts.find((p) => p.id !== productId);
    if (otherProduct) {
      // Convert other product's options to numbers for comparison
      const otherProductOptions = otherProduct.options.map((opt) => ({
        value: parseInt(opt.emails.replace(/,/g, '')),
        original: opt.emails,
      }));

      // Find the best matching option
      let bestMatch = otherProductOptions[0].original;

      // If the exact value exists, use it
      const exactMatch = otherProductOptions.find((opt) => opt.value === selectedValue);
      if (exactMatch) {
        bestMatch = exactMatch.original;
      } else {
        // Find the closest lower value
        const lowerOptions = otherProductOptions.filter((opt) => opt.value <= selectedValue);
        if (lowerOptions.length > 0) {
          bestMatch = lowerOptions[lowerOptions.length - 1].original;
        }
      }

      // Update the other product's value
      newOptions[otherProduct.id] = bestMatch;
    }

    // Call the parent's onOptionChange with the new options
    onOptionChange(newOptions);
  };

  const renderPricingCard = (product, index) => {
    const selectedProductOption = getSelectedProductOption(product.id);
    const isPremium = product.id === 'dmarc-premium';

    return (
      <div
        className={`card product ${isPremium ? 'premium' : ''}`}
        style={{ position: 'relative', overflow: 'visible' }}
      >
        {isPremium && (
          <div
            style={{
              height: '37px',
              left: '50%',
              position: 'absolute',
              top: '-12px',
              transform: 'translateX(-50%)',
              width: '242px',
              zIndex: 2,
            }}
          >
            <Image
              src="/images/ribbon-most-popular.svg"
              alt="Most Popular"
              fill
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}
        <div
          className="card-product-top"
          style={{ paddingTop: isPremium ? '0' : '0', position: 'relative', zIndex: 1 }}
        >
          <div className="mg-bottom-16px">
            <div className="card-product-title-wrapper">
              <h2 className="text-400 bold color-accent-1 mg-bottom-0">{product.name}</h2>
              <div className="flex-vertical children-wrap">
                <div
                  className="text-300 color-neutral-600"
                  style={{ textDecoration: 'line-through', marginBottom: '8px' }}
                >
                  MSRP ${selectedProductOption.msrp.toFixed(2)}
                </div>
                <div className="text-400 bold color-neutral-800">
                  ${selectedProductOption.price.toFixed(2)}/month
                </div>
              </div>
            </div>
          </div>
          <div className="inner-container _322px">
            <p className="mg-bottom-0">{product.description}</p>
          </div>
        </div>
        <div className="divider card-product"></div>
        <div className="card-product-bottom">
          <div className="mg-bottom-24px">
            <div className="text-200 bold color-accent-1">What's included</div>
          </div>
          <div className="mg-bottom-38px">
            <div className="w-layout-grid grid-1-column">
              <div className="feature-wrapper">
                <div className="line-rounded-icon feature-icon"></div>
                <div
                  className="text-200 medium color-neutral-800"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Select
                    value={selectedOption[product.id]}
                    style={{ width: 150, marginRight: '8px' }}
                    size="large"
                    onChange={(value) => handleOptionChange(value, product.id)}
                  >
                    {product.options.map((option) => (
                      <Option key={option.emails} value={option.emails}>
                        {option.emails}
                      </Option>
                    ))}
                  </Select>
                  Emails
                </div>
              </div>

              {product.bullets.map((bullet, index) => (
                <div key={index} className="feature-wrapper">
                  <div className="line-rounded-icon feature-icon"></div>
                  <div className="text-200 medium color-neutral-800">{bullet}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mg-bottom-40px">
            <div className="mg-bottom-12px">
              <div className="card-product-price-display">
                <div className="display-2">$</div>
                <div className="display-2">{selectedProductOption.price.toFixed(2)}</div>
                <div className="text-200 mg-bottom-12px">/month</div>
              </div>
            </div>
            <div className="text-200 medium color-neutral-600">
              $
              {selectedProductOption.annualPrice.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              per year / billed annually
            </div>
          </div>
          <div className="mg-top-auto">
            <button
              className="btn-primary w-button width-100"
              onClick={() => handleBuyNow(product)}
            >
              Buy Now
              <span className="line-rounded-icon link-icon-right"></span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEnterpriseCard = () => (
    <div className="card product popular">
      <div className="card-product-top">
        <div className="mg-bottom-16px">
          <div className="card-product-title-wrapper">
            <h2 className="text-400 bold color-neutral-100 mg-bottom-0">Enterprise</h2>
            <div className="flex-horizontal children-wrap">
              <div className="text-400 bold color-neutral-500">
                Contact Us
                <span className="line-rounded-icon link-icon-right"></span>
              </div>
            </div>
          </div>
        </div>
        <div className="inner-container _322px">
          <p className="color-neutral-300 mg-bottom-0">
            Managed DMARC Services by EasyDMARC's Experts & Engineers to reach maximum enforcement,
            brand protection & email authenticity. Our comprehensive approach utilizes DMARC, BIMI &
            VMCs to secure your email ecosystem, enhance deliverability, and strengthen your brand's
            online presence.
          </p>
        </div>
      </div>
      <div className="divider card-product bg-neutral-700"></div>
      <div className="card-product-bottom">
        <div className="mg-bottom-24px">
          <div className="text-200 bold color-neutral-100">What's included</div>
        </div>
        <div className="mg-bottom-38px">
          <div className="w-layout-grid grid-1-column">
            <div className="feature-wrapper">
              <div className="line-rounded-icon feature-icon"></div>
              <div className="text-200 medium color-neutral-300">Up to Unlimited Emails</div>
            </div>
            <div className="feature-wrapper">
              <div className="line-rounded-icon feature-icon"></div>
              <div className="text-200 medium color-neutral-300">Custom Quantity of Domains</div>
            </div>
            <div className="feature-wrapper">
              <div className="line-rounded-icon feature-icon"></div>
              <div className="text-200 medium color-neutral-300">Up to Unlimited Data History</div>
            </div>
            <div className="feature-wrapper">
              <div className="line-rounded-icon feature-icon"></div>
              <div className="text-200 medium color-neutral-300">Up to Unlimited Users</div>
            </div>
          </div>
        </div>
        <div className="mg-top-auto">
          <Link href="/contact" className="btn-primary w-button width-100 btn-charcoal-light">
            Contact Us
            <span className="line-rounded-icon link-icon-right"></span>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div id="pricing" className="pricing-wrapper w-dyn-items">
      {[...dmarcProducts, { id: 'enterprise', name: 'Enterprise' }].map((product, index) => (
        <motion.div
          key={product.id}
          role="listitem"
          className="card-product-item w-dyn-item"
          custom={index}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          {product.id === 'enterprise' ? renderEnterpriseCard() : renderPricingCard(product, index)}
        </motion.div>
      ))}
    </div>
  );
};

export default DMARCPricing;
